// Lib
import React, { ReactElement, useEffect } from 'react';
import { ConnectedProps, connect } from 'react-redux';

// Utils
import platformSingleton from '../../platform/platformSingleton';
import useIsMountedRef from '../../utils/react/useIsMountedRef';
import { getSizeIn } from '../../../common/files/fileSizeUtils';
import { getSessionId } from '../../device/deviceSessionService';
import { isPlatformNativeApp } from '../../platform/utils/platformDetailsUtils';
import { getExportErrorText } from './exportErrorUtils';

// Actions
import { exportBoardMedia } from './exportPopupActions';
import { closePopup, openPopup } from '../../components/popupPanel/popupActions';

// Components
import ExportPopupExportButton from './ExportPopupExportButton';
import FileIcon from '../../components/fileIcon/FileIcon';

// Constants
import { TIMES } from '../../../common/utils/timeUtil';
import { PopupIds } from '../../components/popupPanel/popupConstants';
import { DOWNLOADING_STATUSES } from './exportConstants';
import { FILE_TYPES_INFO } from '../../../common/files/fileConstants';

// Styles
import './ExportPopupExportButton.scss';

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchExportBoardMedia: (args: { elementId: string; sessionId: string }) => dispatch(exportBoardMedia(args)),
    dispatchClosePopup: () => dispatch(closePopup(PopupIds.EXPORT)),
    dispatchShowExportPopup: () => dispatch(openPopup(PopupIds.EXPORT_BOARD)),
});

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type MediaExportPopupButtonProps = PropsFromRedux & {
    children: ReactElement | string;
    elementId: string;
    totalFileSize: number;
    currentBoardHasThirdPartyImages: boolean;
    boardExports: Immutable.Map<string, any>;
    disabled: boolean;
    exportType: string;
};

const MediaExportPopupButton = (props: MediaExportPopupButtonProps): ReactElement => {
    const {
        elementId,
        children,
        disabled,
        dispatchExportBoardMedia,
        dispatchClosePopup,
        dispatchShowExportPopup,
        currentBoardHasThirdPartyImages,
        totalFileSize,
        boardExports,
    } = props;

    const [status, setStatus] = React.useState<string | null>(null);
    const [errorText, setErrorText] = React.useState<string | null>(null);

    useEffect(() => {
        if (!boardExports.isEmpty()) setStatus(null);
    }, [boardExports]);

    const isMountedRef = useIsMountedRef();
    const isLargeFile = Number(getSizeIn('MB', totalFileSize)) > 50;
    const sessionId = getSessionId();

    // if server doesnt respond for small files in time reset state
    const resetExportingStateOnTimeout = () => {
        if (isMountedRef.current) {
            setTimeout(() => {
                setStatus(null);
            }, TIMES.MINUTE * 1);
        }
    };

    const onClick = async () => {
        setStatus(DOWNLOADING_STATUSES.GENERATING);

        try {
            await dispatchExportBoardMedia({
                elementId,
                sessionId,
            });
            if (
                isPlatformNativeApp(platformSingleton) ||
                isLargeFile ||
                isNaN(totalFileSize) ||
                currentBoardHasThirdPartyImages
            ) {
                dispatchClosePopup();
            }
            if (isLargeFile || isNaN(totalFileSize) || currentBoardHasThirdPartyImages) {
                dispatchShowExportPopup();
            } else {
                resetExportingStateOnTimeout();
            }
        } catch (err) {
            setStatus(DOWNLOADING_STATUSES.ERROR);
            const text = getExportErrorText(err);

            setErrorText(text);
            if (isMountedRef.current) {
                setTimeout(() => {
                    setStatus(null);
                }, TIMES.SECOND * 2);
            }
        }
    };

    return (
        <ExportPopupExportButton status={status} errorText={errorText} onClickFn={onClick} disabled={disabled}>
            <FileIcon fileType={FILE_TYPES_INFO.ZIP.type} iconStyle="mini" disabled={disabled} />

            <span>{children as ReactElement}</span>
        </ExportPopupExportButton>
    );
};

export default connector(MediaExportPopupButton);
